import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} ShowMoreRefs
 * @property {HTMLElement} showMoreButton - The button to toggle visibility of the items
 * @property {HTMLElement[]} showMoreItems - The hidden items to show and hide
 * @property {HTMLElement} showMoreContent - The content container to measure and animate
 */

/**
 * A custom element that manages showing and hiding excess content items.
 *
 * @extends {Component<ShowMoreRefs>}
 *
 * A2D FIX 2026-08-20. Three separate bugs were making collection filters feel broken.
 *
 * BUG 1 — dead buttons.
 * snippets/list-filter.liquid decides whether to render the button from
 * `filter.values.size`, which counts EVERY value Shopify returns including those
 * with count == 0. A separate A2D override (added 2026-05-17 to stop cross-brand
 * submodel bleed) then `continue`s past those zero-count values in the render
 * loop. A facet with 30 raw values but only 5 real matches still got a button,
 * and since no rendered item ever passed the visible-item threshold, nothing was
 * tagged ref="showMoreItems[]". `requiredRefs` then failed, the component never
 * initialised, on:click="/toggle" was never bound, and the button did nothing.
 *
 * BUG 2 — state desync.
 * `#expanded` was only flipped inside the animation's onfinish handler, while
 * data-expanded and aria-expanded were written from the value BEFORE the flip.
 * A cancelled animation left flag and DOM disagreeing and the control jammed.
 *
 * BUG 3 — collapse never applied.
 * Re-hiding the items also lived in onfinish. When that handler did not run,
 * "Show less" flipped the label and aria state but left every item on screen.
 *
 * Fixes:
 *   1. showMoreItems is no longer required, so a partially-formed component
 *      cannot silently kill itself.
 *   2. Nothing to reveal => hide the button (inline display:none, because the
 *      theme sets an explicit display on .show-more__button which beats
 *      the [hidden] attribute).
 *   3. `#expanded` flips synchronously and is the single source of truth.
 *   4. The hidden class is added and removed SYNCHRONOUSLY in the click
 *      handler. The height animation is now purely cosmetic on the container,
 *      so if it is cancelled, skipped, or unsupported the filter list is still
 *      in the correct state.
 */

class ShowMoreComponent extends Component {
  requiredRefs = ['showMoreButton', 'showMoreContent'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {boolean} */
  #disableOnDesktop = false;

  /** @type {number} */
  #collapsedHeight = 0;

  /** @type {'mobile:hidden' | 'hidden'} */
  #disabledClass = 'hidden';

  /** @type {Animation | undefined} */
  #animation;

  /** @constant {number} */
  #animationSpeed = 300;

  /** @type {'MOBILE' | 'DESKTOP'} */
  get #currentBreakpoint() {
    return isMobileBreakpoint() ? 'MOBILE' : 'DESKTOP';
  }

  /**
   * Items this component can reveal. Always an array. Queries the DOM directly
   * so it stays correct even if the ref map is stale after a section re-render.
   * @type {HTMLElement[]}
   */
  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[ref="showMoreItems[]"]')]);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#updateBreakpointState();

    const items = this.#items;

    // Trust the DOM rather than assuming a starting state.
    this.#expanded = items.length > 0 && items.every((item) => !item.classList.contains(this.#disabledClass));

    this.#hideButtonWhenNothingToReveal();
  }

  /**
   * A "Show more" control with nothing behind it is a dead control. Hide it
   * rather than leave something a customer can click for no effect.
   */
  #hideButtonWhenNothingToReveal = () => {
    if (this.#items.length > 0) return;

    const button = this.refs?.showMoreButton;
    if (button) {
      button.style.setProperty('display', 'none', 'important');
      button.setAttribute('aria-hidden', 'true');
      button.tabIndex = -1;
    }
    this.dataset.noItems = 'true';
  };

  #updateBreakpointState = () => {
    this.#disableOnDesktop = this.dataset.disableOnDesktop === 'true';
    this.#disabledClass = this.#disableOnDesktop ? 'mobile:hidden' : 'hidden';
  };

  /**
   * Clears the inline styles the height animation needs. Safe to call twice.
   */
  #clearInlineHeight = () => {
    const content = this.refs?.showMoreContent;
    if (!content) return;
    content.style.removeProperty('height');
    content.style.overflow = '';
  };

  /**
   * Cosmetic height transition on the container. Never carries state.
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight = (startHeight, endHeight) => {
    const { showMoreContent } = this.refs;

    if (this.#animation) {
      this.#animation.onfinish = null;
      this.#animation.oncancel = null;
      this.#animation.cancel();
    }

    if (typeof showMoreContent.animate !== 'function') return;

    showMoreContent.style.overflow = 'hidden';

    try {
      this.#animation = showMoreContent.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: this.#animationSpeed, easing: 'ease-in-out' }
      );
      this.#animation.onfinish = this.#clearInlineHeight;
      this.#animation.oncancel = this.#clearInlineHeight;
    } catch (error) {
      this.#clearInlineHeight();
    }
  };

  /**
   * Toggles the expansion state of the content.
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    this.#updateBreakpointState();

    if (this.#currentBreakpoint === 'DESKTOP' && this.#disableOnDesktop) return;

    const items = this.#items;
    if (items.length === 0) return;

    const { showMoreContent } = this.refs;
    const willExpand = !this.#expanded;
    const startHeight = showMoreContent.offsetHeight;

    let endHeight;

    if (willExpand) {
      this.#collapsedHeight = startHeight;
      items.forEach((item) => item.classList.remove(this.#disabledClass));
      endHeight = showMoreContent.scrollHeight;
    } else {
      endHeight = this.#collapsedHeight || 0;
      // Applied synchronously so the collapse cannot depend on an animation
      // callback that may never fire.
      items.forEach((item) => item.classList.add(this.#disabledClass));
    }

    this.#expanded = willExpand;
    this.dataset.expanded = String(willExpand);
    this.refs.showMoreButton.setAttribute('aria-expanded', String(willExpand));

    this.#animateHeight(startHeight, endHeight);
  };
}

if (!customElements.get('show-more-component')) {
  customElements.define('show-more-component', ShowMoreComponent);
}
